import { useState } from "react";
import { Header } from "../components/promill/Header";
import { Footer } from "../components/promill/Footer";
import { Plus, Minus, MessageCircle } from "lucide-react";
import { WHATSAPP_URL } from "../data/promill";

const FAQS = [
    {
        cat: "Product",
        items: [
            { q: "What makes ProMill different from other health-food brands?", a: "We're 100% millet-first. Every meal is built on ancient Indian grains (jowar, bajra, ragi, foxtail, kodo) with 22g+ clean protein, zero refined flour and zero seed oils. It's fast food, rebuilt cleaner." },
            { q: "Are ProMill meals vegetarian?", a: "We offer vegetarian, vegan and non-vegetarian options. Every product is clearly labelled — filter by diet on our Menu page." },
            { q: "Do you use any preservatives or artificial ingredients?", a: "Never. All our sauces are made in-house with cold-pressed oils, real spices and fresh produce. Zero artificial preservatives, additives or MSG." },
            { q: "Is ProMill gluten-friendly?", a: "Millets are naturally gluten-friendly. Most of our bases (foxtail, ragi, kodo, jowar) are gluten-free grains, ideal for gluten-sensitive diets." },
        ],
    },
    {
        cat: "Ordering",
        items: [
            { q: "How do I order?", a: "We're gearing up for full digital ordering. For now, please reach out via our Contact page or WhatsApp and our team will connect with you." },
            { q: "When will stores open?", a: "Our first ProMill experience centers launch in 2026 across Bengaluru, Mumbai and Delhi NCR. Join our waitlist to hear first." },
            { q: "Do you deliver?", a: "Delivery rolls out alongside our store network. Waitlist members get priority access on day one." },
        ],
    },
    {
        cat: "Nutrition",
        items: [
            { q: "How much protein does each meal have?", a: "Every ProMill meal carries at least 22g of clean protein — most bowls and shakes range from 25g to 35g. Full macros are visible per product." },
            { q: "Are your meals calorie-controlled?", a: "Yes. Every meal is capped at 500 kcal. See the Nutrition page for our detailed macro philosophy." },
            { q: "Can I see full ingredient lists?", a: "Absolutely. Every product page shows complete ingredients, allergens and nutrition breakdown. Radical transparency is our commitment." },
        ],
    },
    {
        cat: "Company",
        items: [
            { q: "Where is ProMill based?", a: "Our HQ is in Bengaluru. We source millets directly from farmers across Karnataka, Andhra Pradesh and Rajasthan." },
            { q: "Do you offer franchise partnerships?", a: "Yes — visit our Franchise page for details and submit your enquiry." },
            { q: "How does ProMill support sustainability?", a: "We're aligned with UN SDGs 2, 3 and 12. Compostable packaging, farmer-first sourcing, water-efficient grains — good food, real impact." },
        ],
    },
];

const Item = ({ q, a, idx }) => {
    const [open, setOpen] = useState(false);
    return (
        <div data-testid={`faq-item-${idx}`} className="border-b border-pm-black/10 last:border-b-0">
            <button
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                className="w-full py-5 flex items-start justify-between gap-4 text-left"
            >
                <span className="font-bold text-pm-black text-base md:text-lg pr-4">{q}</span>
                <span className="shrink-0 h-8 w-8 rounded-full bg-pm-yellow/20 text-pm-green flex items-center justify-center">
                    {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </span>
            </button>
            {open && <p className="pb-5 pr-12 text-pm-black/70 leading-relaxed">{a}</p>}
        </div>
    );
};

const FAQPage = () => (
    <div data-testid="faq-page" className="bg-pm-cream min-h-screen">
        <Header />
        <section className="pt-10 md:pt-16 pb-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <span className="inline-block text-pm-orange font-bold tracking-[0.25em] uppercase text-[11px] mb-3">FAQs</span>
                <h1 className="text-4xl md:text-6xl font-extrabold tracking-[-0.02em] text-pm-black leading-tight">
                    Questions, <span className="text-pm-green">answered.</span>
                </h1>
                <p className="mt-3 text-pm-black/65 max-w-2xl mx-auto">
                    Everything you'd want to know about ProMill — meals, macros, ordering and impact.
                </p>
            </div>
        </section>
        <section className="pb-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {FAQS.map((group, gi) => (
                    <div key={group.cat} className="mb-10">
                        <h2 data-testid={`faq-group-${gi}`} className="text-xl font-extrabold text-pm-green uppercase tracking-widest mb-3">{group.cat}</h2>
                        <div className="bg-white rounded-2xl border border-pm-black/8 px-6">
                            {group.items.map((it, i) => <Item key={i} q={it.q} a={it.a} idx={`${gi}-${i}`} />)}
                        </div>
                    </div>
                ))}
                <div className="mt-12 bg-pm-green rounded-2xl p-8 text-center text-white">
                    <h3 className="text-2xl md:text-3xl font-extrabold">Still have a question?</h3>
                    <p className="mt-2 text-white/85">Reach us on WhatsApp — we reply fast.</p>
                    <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="faq-whatsapp-cta" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-pm-yellow text-pm-black font-bold hover:bg-pm-yellowBright transition-colors">
                        <MessageCircle className="w-4 h-4" />
                        Ask on WhatsApp
                    </a>
                </div>
            </div>
        </section>
        <Footer />
    </div>
);

export default FAQPage;
